<script setup lang="ts">
  import { UIContentWrap, UIInput, UISwitch } from '@gohighlevel/ghl-ui'
  import { computed, defineEmits, defineProps, PropType, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useContentLibraryFactory, useHtmlToCanvasFactory, useNotify } from '../../../composition'
  import { CONTENT_LIBRARY_INTERNAL_AGENCY_ID } from '../../../const'
  import { GroupFields, GroupFieldsOptions, IElement, UpdateDocument } from '../../../types'
  import { ContentLibraryCategoryEnum, ModalEvents } from '../../../types/components'
  import { flattenTreeToMap, sanitizePagesForContentLibrary } from '../../../utils/helpers'
  import { CustomModal } from '../CustomModal'

  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    isFromPage: {
      type: Boolean,
      default: false,
    },
    pageElement: {
      type: Object as PropType<IElement<any>[]>,
      default: () => ({}),
    },
    groups: {
      type: Array as PropType<GroupFields<GroupFieldsOptions>[]>,
      default: () => [],
    },
    locationId: {
      type: String,
      default: '',
    },
    element: {
      type: Object as PropType<IElement<any>[]>,
      default: () => ({}),
    },
    isInternal: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: String,
      default: '',
    },
  })
  const i18nLocale = useI18n()
  const { t } = i18nLocale
  const { handleErrorMessage } = useNotify()
  const emits = defineEmits([ModalEvents.ON_SUCCESS, ModalEvents.ON_CANCEL])
  const contentName = ref('')
  const featured = ref(false)
  const thumbnailBase64 = ref('')
  const loading = ref(false)

  const { addToContentLibrary } = useContentLibraryFactory()
  const { generateThumbnailForPage, generateHiddenThumbnail } = useHtmlToCanvasFactory()

  const onCancel = () => {
    emits(ModalEvents.ON_CANCEL)
  }

  const groupsExistOnPage = () => {
    const { map: pageElementMap } = flattenTreeToMap(props.isFromPage ? props.pageElement : props.element)
    const groupsExistOnPage = props.groups.filter(group => {
      return group.children.every(child => {
        return pageElementMap[child.elementId]
      })
    })
    return groupsExistOnPage
  }

  const onSuccess = async () => {
    try {
      loading.value = true
      const pages = sanitizePagesForContentLibrary([props.pageElement])
      const groups = groupsExistOnPage()
      if (props.isFromPage) {
        const base64Image = await generateThumbnailForPage(props.pageElement?.id)
        thumbnailBase64.value = base64Image || ''
      } else {
        const base64Image = await generateHiddenThumbnail(props.element as IElement)
        thumbnailBase64.value = base64Image || ''
      }
      if (!thumbnailBase64.value) {
        handleErrorMessage(`${t('contentLibrary.failedToGenerate')}`)
        emits(ModalEvents.ON_SUCCESS)
        return
      }
      if (!contentName.value) {
        handleErrorMessage(`${t('validations.required.contentName')}`)
        return
      }

      const payload: UpdateDocument = {
        name: contentName.value,
        groups,
        pages,
        locationId: props.locationId,
        thumbnail: thumbnailBase64.value || '',
        contentLibraryCategory: featured.value === true ? ContentLibraryCategoryEnum.featured : ContentLibraryCategoryEnum.custom,
      } as unknown as UpdateDocument
      await addToContentLibrary(payload)
      emits(ModalEvents.ON_SUCCESS)
    } catch (e) {
      console.error(e)
    } finally {
      loading.value = false
    }
  }

  watch(
    () => props.show,
    val => {
      if (val) {
        contentName.value = ''
      }
    }
  )

  const enableFeatured = computed(() => {
    return props.isInternal && props.companyId && CONTENT_LIBRARY_INTERNAL_AGENCY_ID.includes(props.companyId)
  })
</script>

<template>
  <CustomModal
    :width="500"
    :show="props.show"
    :modal-type="'primary'"
    :title="$t('contentLibrary.addToContentLib')"
    @on-cancel="onCancel"
    @on-success="onSuccess"
    id="add-to-content-library-modal"
    :cancel-action-text="$t('common.cancel')"
    :success-action-text="$t('contentLibrary.addToContentLib')"
    :header-icon="false"
    :loading="loading"
    :close-on-success="false"
    :disabled="loading || !contentName"
  >
    <UIContentWrap :locale="i18nLocale?.locale?.value || 'en-US'" class="p-0" style="display: contents">
      <div class="text-left">
        <span>{{ $t('contentLibrary.contentName') }}</span>
        <UIInput
          v-model="contentName"
          :label="$t('contentLibrary.contentName')"
          id="input-content-library-name"
          :placeholder="$t('contentLibrary.contentName')"
          :type="'text'"
          :maxlength="150"
          :minlength="1"
          size="medium"
          :show-count="true"
          :class="'mb-4 mt-2'"
        />
      </div>
    </UIContentWrap>
    <div class="w-full mb-4 mt-2" v-if="enableFeatured">
      <div class="flex flex-row">
        <div class="w-4/5">
          <span class="text-sm text-gray-600">
            {{ $t('contentLibrary.isFeaturedDescription') }}
          </span>
        </div>
        <div class="w-1/5 text-right">
          <UISwitch id="contentLibrary.featured" v-model:value="featured" />
        </div>
      </div>
    </div>
  </CustomModal>
</template>
